import { sectionRenderer } from '@theme/section-renderer';

/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 20;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}

/**
 * Recently Viewed Products Slider Component
 */
class RecentlyViewedSlider {
  /**
   * @param {HTMLElement} wrapper
   */
  constructor(wrapper) {
    
    this.wrapper = wrapper;
    this.section = wrapper.closest('section');
    this.swiperWrapper = wrapper.querySelector('.swiper-wrapper');
    this.sectionId = wrapper.dataset.sectionId || '';
    this.maxProducts = parseInt(wrapper.dataset.maxProducts || '4') || 4;
    this.swiper = null;
    this.init();
  }

  async init() {
    
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length === 0) {
      this.wrapper.hidden = true;
      if (this.section) {
        this.section.hidden = true;
      }
      return;
    }
    const hasProducts = await this.loadProducts(viewedProducts);
    
    if (!hasProducts) {
      console.error('[recently-viewed] Failed to load products, hiding section');
      this.wrapper.hidden = true;
      if (this.section) {
        this.section.hidden = true;
      }
      return;
    }
    this.wrapper.hidden = false;
    if (this.section) {
      this.section.hidden = false;
    }
    this.initSwiper();
  }

  /**
   * @param {string[]} productIds
   * @returns {Promise<boolean>}
   */
  async loadProducts(productIds) {
    try {
      
      const searchQuery = productIds
        .slice(0, this.maxProducts)
        .map((/** @type {string} */ id) => `id:${id}`)
        .join(' OR ');
      
      // Wait for Theme to be available with timeout
      const searchUrl = await this.getSearchUrl();
      if (!searchUrl) {
        console.error('[recently-viewed] Could not get search URL');
        return false;
      }
      
      const url = new URL(searchUrl, location.origin);
      url.searchParams.set('q', searchQuery);
      url.searchParams.set('resources[type]', 'product');

      const sectionHTML = await sectionRenderer.getSectionHTML('search-results', false, url);

      if (!sectionHTML) {
        console.error('[recently-viewed] No HTML returned from search');
        return false;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, 'text/html');
      const productCards = doc.querySelectorAll('.product-card[data-product-id]');

      if (productCards.length === 0) {
        return false;
      }

      if (!this.swiperWrapper) {
        console.error('[recently-viewed] Swiper wrapper not found');
        return false;
      }

      this.swiperWrapper.innerHTML = '';

      const orderedProducts = this.orderProductsByIds(
        productIds.slice(0, this.maxProducts),
        productCards
      );

      orderedProducts.forEach((productCard, index) => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.innerHTML = productCard.outerHTML;
        if (this.swiperWrapper) {
          this.swiperWrapper.appendChild(slide);
        }
      });
      return orderedProducts.length > 0;
    } catch (error) {
      console.error('[recently-viewed] Error loading products:', error, {
        message: error.message,
        stack: error.stack
      });
      return false;
    }
  }

  /**
   * Gets the search URL, waiting for Theme to be available if needed
   * @returns {Promise<string|null>}
   */
  async getSearchUrl() {
    // Try to get from Theme object
    // @ts-ignore - Theme is a global object
    if (window.Theme?.routes?.search_url) {
      // @ts-ignore
      // @ts-ignore
      return window.Theme.routes.search_url;
    }

    // Wait for Theme to be available (max 500ms for faster loading)
    for (let i = 0; i < 5; i++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      // @ts-ignore
      if (window.Theme?.routes?.search_url) {
        // @ts-ignore
        // @ts-ignore
        return window.Theme.routes.search_url;
      }
    }

    // Fallback to hardcoded search URL (fast fallback)
    return '/search';
  }

  /**
   * @param {string[]} ids
   * @param {NodeListOf<Element>} products
   * @returns {HTMLElement[]}
   */
  orderProductsByIds(ids, products) {
    /** @type {HTMLElement[]} */
    const ordered = [];
    const productsMap = new Map();

    products.forEach((/** @type {Element} */ product) => {
      if (product instanceof HTMLElement) {
        const productId = product.dataset.productId;
        if (productId) {
          productsMap.set(productId.toString(), product);
        }
      }
    });

    ids.forEach((/** @type {string} */ id) => {
      const product = productsMap.get(id.toString());
      if (product) {
        ordered.push(product);
      }
    });

    return ordered;
  }

  initSwiper() {
    
    // @ts-ignore - Swiper is loaded globally
    if (typeof window.Swiper === 'undefined') {
      console.error('[recently-viewed] Swiper is not loaded on window object');
      return;
    }

    const prevButton = this.wrapper.querySelector('.swiper-button-prev-custom');
    const nextButton = this.wrapper.querySelector('.swiper-button-next-custom');
    const swiperEl = this.wrapper.querySelector('.recently-viewed-swiper');

    // @ts-ignore - Swiper is loaded globally
    this.swiper = new window.Swiper(swiperEl, {
      slidesPerView: 1,
      spaceBetween: 20,
      navigation: {
        nextEl: nextButton,
        prevEl: prevButton,
        disabledClass: 'swiper-button-disabled',
      },
      breakpoints: {
        640: {
          slidesPerView: 2,
          spaceBetween: 20,
        },
        768: {
          slidesPerView: 3,
          spaceBetween: 20,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 20,
        },
      },
    });
  }
}

// Auto-initialize on DOM ready
function initRecentlyViewedSliders() {
  
  const wrappers = document.querySelectorAll('.recently-viewed-slider-wrapper');
  
  if (wrappers.length === 0) {
    return;
  }
  
  wrappers.forEach((/** @type {Element} */ wrapper, index) => {
    if (wrapper instanceof HTMLElement) {
      new RecentlyViewedSlider(wrapper);
    } else {
      console.error(`[recently-viewed] Wrapper ${index + 1} is not an HTMLElement`);
    }
  });
}

// Initialize based on DOM state
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRecentlyViewedSliders);
} else {
  initRecentlyViewedSliders();
}
